// PhotographsModal.js - Updated frontend (optional)
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Divider,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import SaveIcon from "@mui/icons-material/Save";
import { useParams } from "react-router-dom";
import { useMutation } from "react-query";
import { createPhotograph } from "../../../api/services/photographs";
import { enqueueSnackbar } from "notistack";
import ImageUpload from "../../../components/CustomForm/ImageUpload";

const PhotographsModal = ({ show, onHide, refetch }) => {
  const { id: clientId } = useParams();
  const [imageUrl, setImageUrl] = useState("");
  const [caption, setCaption] = useState("");

  const postMutation = useMutation(createPhotograph, {
    onSuccess: () => {
      enqueueSnackbar("Photograph added successfully", { variant: "success" });
      handleClose();
      refetch();
    },
    onError: (error) => {
      const message = error?.response?.data?.message || "Failed to add photograph";
      enqueueSnackbar(message, { variant: "error" });
    },
  });

  const handleImageUpload = (e) => {
    const { fileData } = e.target;
    if (fileData?.url) {
      setImageUrl(fileData.url);
    }
  };

  const handleCaptionChange = (e) => {
    setCaption(e.target.value);
  };

  const handleSave = (e) => {
    e.preventDefault();

    if (!imageUrl) {
      enqueueSnackbar("Please upload an image", { variant: "warning" });
      return;
    }

    const data = {
      image: imageUrl,
      caption: caption,
      client: clientId,
    };

    postMutation.mutate(data);
  };

  const handleClose = () => {
    setImageUrl("");
    setCaption("");
    onHide();
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0px 24px 48px -12px rgba(16, 24, 40, 0.25)",
        },
      }}
    >
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            pb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "10px",
                bgcolor: "#EFF8FF",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PhotoCameraIcon sx={{ color: "#2469F0", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ color: "#1A1A2E" }}>
                Add Photograph
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280" }}>
                Upload a new photograph to gallery
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "#9CA3AF",
              "&:hover": { bgcolor: "#F3F4F6", color: "#4B5563" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ opacity: 0.4 }} />
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2 }}>
        <form onSubmit={handleSave}>
          <Stack spacing={2.5}>
            <Box>
              <ImageUpload
                name="image"
                label="Upload Photograph"
                onChange={handleImageUpload}
                value={imageUrl}
                accept="image/jpeg,image/png,image/gif"
                maxSize={10 * 1024 * 1024}
                folder="client_photographs"
                circular={false}
                preview={true}
                required={true}
              />
            </Box>

            <TextField
              label="Caption"
              name="caption"
              fullWidth
              value={caption}
              onChange={handleCaptionChange}
              placeholder="Enter a caption for this photograph..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  "&:hover fieldset": {
                    borderColor: "#2469F0",
                  },
                },
              }}
            />
          </Stack>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, gap: 1.5 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={postMutation.isLoading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderRadius: "10px",
            borderColor: "#D0D5DD",
            color: "#344054",
            "&:hover": {
              bgcolor: "#F9FAFB",
              borderColor: "#98A2B3",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={postMutation.isLoading}
          startIcon={postMutation.isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            bgcolor: "#2469F0",
            px: 3,
            "&:hover": {
              bgcolor: "#1A5BD4",
              boxShadow: "0px 4px 12px rgba(36, 105, 240, 0.3)",
            },
          }}
        >
          {postMutation.isLoading ? "Uploading..." : "Upload Photograph"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhotographsModal;